import React, { createContext, useCallback, useContext, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import type { RootState } from "../../../../redux/store";
import type { EquipmentListProps, KeyHandoverServiceType } from "../../../../types/baseTypes";
import { useQuery, type QueryObserverResult } from "@tanstack/react-query";
import { fetchLocationEquipment } from "../../../../api/locations/locationEquipmentClient";
import {
  assetGroupActivated,
  assetGroupDeactivated,
  setKeyHandoverSelectedService,
  setNextEnabled,
  setSelectedGroup,
} from "../../../../redux/slice/bookingCreation/bookingCreationSlice";

/**
 * State exposed by KeyHandoverServiceContext
 */
type KeyHandoverServiceContextState = {
  /**
   * Services (equipment groups incl. price) for the currently selected location.
   * Undefined when no location is selected or while initial load.
   */
  services: EquipmentListProps[] | undefined;
  /** Currently selected service (kept in Redux for flow compatibility). */
  selected: EquipmentListProps | null;
  /** Loading state while fetching services for the selected location. */
  isLoading: boolean;
  /** Error state for the fetch operation. */
  isError: boolean;
  /** Error object from the fetch operation, if any. */
  error: unknown | null;
  /** Select a service and advance the booking flow. */
  select: (equipment: EquipmentListProps) => void;
  /** Clear current selection and disable next step. */
  deselect: () => void;
  /** Re-trigger the services fetch. */
  refetch: () => Promise<QueryObserverResult<EquipmentListProps[], unknown>>;
};

const KeyHandoverServiceContext = createContext<KeyHandoverServiceContextState | undefined>(
  undefined
);

export function KeyHandoverServiceProvider({
  children,
  typesFilter,
}: {
  children: React.ReactNode;
  /** Optional server-side filter for equipment types, e.g. "Key-Pickup-Only" */
  typesFilter?: string;
}) {
  const dispatch = useDispatch();

  const selectedLocationId = useSelector(
    (state: RootState) => state.bookingCreation.selectedLocation?.id
  );

  // Get customerAgreementId from URL
  const customerAgreementId = (() => {
    if (typeof window !== "undefined") {
      const params = new URLSearchParams(window.location.search);
      return params.get("customerAgreementId") || undefined;
    }
    return undefined;
  })();

  // Load services for selected location via API + TanStack Query
  const {
    data: services,
    isLoading,
    isError,
    error,
    refetch,
  } = useQuery<EquipmentListProps[]>({
    queryKey: ["location-equipment", selectedLocationId, typesFilter ?? null, customerAgreementId ?? null],
    queryFn: () => fetchLocationEquipment(selectedLocationId as string, typesFilter, customerAgreementId),
    enabled: !!selectedLocationId,
    staleTime: 1000 * 60, // 1 minute
  });
  const selected = useSelector(
    (state: RootState) => state.bookingCreation.selectedGroup
  ) as EquipmentListProps | null;

  const deselect = useCallback(() => {
    dispatch(assetGroupDeactivated());
    dispatch(setSelectedGroup(null));
    dispatch(setNextEnabled(false));
  }, [dispatch]);

  const select = useCallback(
    (equipment: EquipmentListProps) => {
      const selectedService = equipment.priceCategoryInfo.name as KeyHandoverServiceType;
      dispatch(setKeyHandoverSelectedService(selectedService));
      dispatch(assetGroupActivated(equipment));
      dispatch(setSelectedGroup(equipment));
      dispatch(setNextEnabled(true));
    },
    [dispatch]
  );

  const value = useMemo<KeyHandoverServiceContextState>(
    () => ({
      services: selectedLocationId ? services : undefined,
      selected,
      isLoading: !!selectedLocationId && isLoading,
      isError,
      error: error ?? null,
      select,
      deselect,
      refetch,
    }),
    [services, selected, selectedLocationId, isLoading, isError, error, select, deselect, refetch]
  );

  return (
    <KeyHandoverServiceContext.Provider value={value}>
      {children}
    </KeyHandoverServiceContext.Provider>
  );
}

export function useKeyHandoverServices() {
  const ctx = useContext(KeyHandoverServiceContext);
  if (!ctx) {
    throw new Error(
      "useKeyHandoverServices must be used within a KeyHandoverServiceProvider"
    );
  }
  return ctx;
}

/**
 * Lightweight hook to access only status from the service context.
 */
export function useKeyHandoverServiceStatus() {
  const ctx = useContext(KeyHandoverServiceContext);
  if (!ctx) {
    throw new Error(
      "useKeyHandoverServiceStatus must be used within a KeyHandoverServiceProvider"
    );
  }
  const { isLoading, isError, error } = ctx;
  return { isLoading, isError, error };
}
